/**
 * Viewer keyboard-shortcut action registry.
 *
 * Each viewer action has a stable id, a category (for grouping in settings),
 * an i18n label key (in the `settings` namespace), and a default key "chord".
 * The runtime handler (see `useViewerShortcuts`) maps a pressed chord → action
 * id → a callback supplied by ViewerShell, so the key bindings are fully
 * user-configurable while the behavior stays in one place.
 */
export const SHORTCUT_ACTIONS = [
    // Tools
    { id: 'tool.scroll', category: 'tools', labelKey: 'shortcuts.action.scroll' },
    { id: 'tool.zoom', category: 'tools', labelKey: 'shortcuts.action.zoom' },
    { id: 'tool.windowLevel', category: 'tools', labelKey: 'shortcuts.action.windowLevel' },
    { id: 'tool.pan', category: 'tools', labelKey: 'shortcuts.action.pan' },
    // Transform
    { id: 'transform.rotate', category: 'transform', labelKey: 'shortcuts.action.rotate' },
    { id: 'transform.rotateCcw', category: 'transform', labelKey: 'shortcuts.action.rotateCcw' },
    {
        id: 'transform.flipHorizontal',
        category: 'transform',
        labelKey: 'shortcuts.action.flipHorizontal',
    },
    {
        id: 'transform.flipVertical',
        category: 'transform',
        labelKey: 'shortcuts.action.flipVertical',
    },
    { id: 'transform.invert', category: 'transform', labelKey: 'shortcuts.action.invert' },
    { id: 'transform.reset', category: 'transform', labelKey: 'shortcuts.action.reset' },
    {
        id: 'annotation.delete',
        category: 'transform',
        labelKey: 'shortcuts.action.deleteAnnotation',
    },
    {
        id: 'annotation.clearAll',
        category: 'transform',
        labelKey: 'shortcuts.action.clearAnnotations',
    },
    {
        id: 'annotation.toggleVisibility',
        category: 'transform',
        labelKey: 'shortcuts.action.toggleAnnotations',
    },
    { id: 'bookmark.toggle', category: 'tools', labelKey: 'shortcuts.action.bookmarkToggle' },
    { id: 'bookmark.next', category: 'tools', labelKey: 'shortcuts.action.bookmarkNext' },
    { id: 'bookmark.prev', category: 'tools', labelKey: 'shortcuts.action.bookmarkPrev' },
    // Layout
    { id: 'layout.single', category: 'layout', labelKey: 'shortcuts.action.layoutSingle' },
    { id: 'layout.twoUp', category: 'layout', labelKey: 'shortcuts.action.layoutTwoUp' },
    { id: 'layout.quad', category: 'layout', labelKey: 'shortcuts.action.layoutQuad' },
    { id: 'layout.threeD', category: 'layout', labelKey: 'shortcuts.action.layout3d' },
    { id: 'layout.mpr', category: 'layout', labelKey: 'shortcuts.action.layoutMpr' },
    // Playback
    { id: 'playback.cine', category: 'playback', labelKey: 'shortcuts.action.cine' },
    { id: 'playback.stepBack', category: 'playback', labelKey: 'shortcuts.action.cineStepBack' },
    {
        id: 'playback.stepForward',
        category: 'playback',
        labelKey: 'shortcuts.action.cineStepForward',
    },
    // Panels / overlays
    { id: 'panel.seriesStrip', category: 'panels', labelKey: 'shortcuts.action.seriesStrip' },
    { id: 'panel.overlays', category: 'panels', labelKey: 'shortcuts.action.overlays' },
    { id: 'panel.referenceLines', category: 'panels', labelKey: 'shortcuts.action.referenceLines' },
    { id: 'panel.echo', category: 'panels', labelKey: 'shortcuts.action.echoPanel' },
    { id: 'help.shortcuts', category: 'panels', labelKey: 'shortcuts.action.shortcutHelp' },
    // Navigation
    { id: 'nav.prevImage', category: 'navigation', labelKey: 'shortcuts.action.prevImage' },
    { id: 'nav.nextImage', category: 'navigation', labelKey: 'shortcuts.action.nextImage' },
    { id: 'nav.firstImage', category: 'navigation', labelKey: 'shortcuts.action.firstImage' },
    { id: 'nav.lastImage', category: 'navigation', labelKey: 'shortcuts.action.lastImage' },
    { id: 'nav.next', category: 'navigation', labelKey: 'shortcuts.action.next' },
    { id: 'nav.prevStudy', category: 'navigation', labelKey: 'shortcuts.action.prevStudy' },
    { id: 'nav.back', category: 'navigation', labelKey: 'shortcuts.action.back' },
    // Report
    {
        id: 'report.open',
        category: 'report',
        labelKey: 'shortcuts.action.openReport',
        bypassInputGuard: true,
    },
    { id: 'report.capture', category: 'report', labelKey: 'shortcuts.action.captureViewport' },
    { id: 'reading.cycleMode', category: 'report', labelKey: 'shortcuts.action.cycleReadingMode' },
];
export const SHORTCUT_ACTIONS_BY_ID = SHORTCUT_ACTIONS.reduce((acc, def) => {
    acc[def.id] = def;
    return acc;
}, {});
export const SHORTCUT_CATEGORY_ORDER = [
    'tools',
    'transform',
    'layout',
    'navigation',
    'playback',
    'panels',
    'report',
];
/**
 * Default key chords. A chord is lowercase, modifier-prefixed, in the fixed
 * order ctrl, alt, shift (see `eventToChord`). Mirrors the previously
 * hardcoded ViewerShell bindings exactly.
 */
export const DEFAULT_KEYBOARD_SHORTCUTS = {
    'tool.scroll': 's',
    'tool.zoom': 'z',
    'tool.windowLevel': 'w',
    'tool.pan': 'p',
    'transform.rotate': 'r',
    'transform.rotateCcw': 'alt+r',
    'transform.flipHorizontal': 'h',
    'transform.flipVertical': 'v',
    'transform.invert': 'i',
    'transform.reset': '0',
    'layout.single': '1',
    'layout.twoUp': '2',
    'layout.threeD': '3',
    'layout.quad': '4',
    'layout.mpr': 'm',
    'playback.cine': 'space',
    'playback.stepBack': 'arrowleft',
    'playback.stepForward': 'arrowright',
    'panel.seriesStrip': 't',
    'panel.overlays': 'o',
    'panel.referenceLines': 'l',
    'panel.echo': 'e',
    'nav.prevImage': 'arrowup',
    'nav.nextImage': 'arrowdown',
    'nav.firstImage': 'home',
    'nav.lastImage': 'end',
    'nav.next': 'shift+n',
    'nav.prevStudy': 'shift+p',
    'nav.back': 'escape',
    'report.open': 'shift+r',
    'report.capture': 'ctrl+shift+c',
    'reading.cycleMode': 'shift+l',
    'bookmark.toggle': 'b',
    'bookmark.next': 'alt+arrowdown',
    'bookmark.prev': 'alt+arrowup',
    // Dispatched by ViewerShell. The registry is single-binding, so Backspace
    // stays a fixed alias in the 2D viewer (lib/shortcuts/deleteKeyAlias.ts)
    // rather than a second chord here.
    'annotation.delete': 'delete',
    'annotation.clearAll': 'shift+delete',
    'annotation.toggleVisibility': 'a',
    // eventToChord for Shift+/ ('?') — pinned in chords.test.ts.
    'help.shortcuts': 'shift+?',
};
